import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { initFrappeClient, FrappeClient } from '@/lib/frappe-client';
import {
  cpLogin,
  cpLogout,
  setCPToken,
  setCPTenantMeta,
  clearCPToken,
  getCPToken,
  getCPRefreshToken,
  getCPServerUrl,
  getCPIsProvisioned,
  getCPStorefrontUrl,
  decodeJwtExpiry,
  decodeJwtPayload,
  refreshCPToken,
  setTokenRefreshCallback,
} from '@/lib/api/controlPlaneClient';
import type { LineCallbackParams, RegisterResponse } from '@/lib/api/controlPlaneClient';
import { sanitizeCompanyDocname } from '@/lib/store/companyDocname';

/** Response shape from CP login/register/google_one_tap — has JWT tokens + tenant info */
type CPTokenResponse = RegisterResponse;
import { cpMyAccess } from '@/features/team/api';
import { clearChatwootSession } from '@/components/ChatwootEmbed/ChatwootEmbed';
import { clearGhostSession } from '@/components/GhostEmbed';
import { clearTwentySession } from '@/components/TwentyCrmEmbed';

type AuthMode = 'direct' | 'control_plane';

// Module-level promise for the in-flight token refresh started during rehydration.
// ensureFreshToken() awaits this so iframe embeds don't race against the refresh.
let _refreshPromise: Promise<void> | null = null;

interface AuthState {
  isAuthenticated: boolean;
  frappeUrl: string | null;
  username: string | null;
  frappeClient: FrappeClient | null;
  hasHydrated: boolean;
  currentTenant: string | null;
  tenantSlug: string | null;
  // Control Plane fields
  authMode: AuthMode;
  cpToken: string | null;
  cpTokenExpiresAt: string | null;
  tenantServerUrl: string | null;
  tenantCompany: string | null;
  isProvisioned: boolean;
  storefrontUrl: string | null;
  // Multi-storefront scope
  activeStorefrontKey: string | null;
  // App access (from CP my_access endpoint)
  appAccess: string[] | null;
  isAdmin: boolean;
  /** Tenant role from CP my_access (Owner / Admin / Member / Viewer) */
  role: string | null;
  appAccessLoaded: boolean;
  installedApps: string[];
  // Support-only tenant: dashboard locked to the Help Center
  supportOnly: boolean;
  // Methods
  setActiveStorefront: (key: string | null) => void;
  login: (url: string, username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginViaControlPlane: (email: string, password: string) => Promise<void>;
  loginViaLine: (params: LineCallbackParams) => void;
  loginWithCPResponse: (response: CPTokenResponse) => void;
  logoutControlPlane: () => Promise<void>;
}

/**
 * Phase 5.3: Fetch tenant from API
 * FrappeClient.call already unwraps the message field
 */
async function fetchUserTenant(client: FrappeClient): Promise<string | null> {
  try {
    const tenant = await client.call<string | null>(
      "zaviago_backend.api.tenant.get_current_user_tenant",
      {}
    );
    return tenant ?? null;
  } catch (e) {
    console.warn("Failed to fetch tenant:", e);
    return null;
  }
}

/**
 * Helper to resolve API URL for FrappeClient
 */
function resolveApiUrl(frappeUrl: string): string {
  const normalizedUrl = frappeUrl.replace(/\/$/, "");

  // For local dev with Vite proxy + local Frappe, route via '/api'
  if (
    import.meta.env.DEV &&
    (normalizedUrl === "http://localhost:8000" ||
      normalizedUrl === "http://127.0.0.1:8000")
  ) {
    return "/api";
  }

  return normalizedUrl;
}

/**
 * Fetch app access from CP and update authStore.
 * Called during rehydration and after login. Non-blocking.
 */
function fetchAndStoreAppAccess(): void {
  cpMyAccess()
    .then((result) => {
      useAuthStore.setState({
        appAccess: result.app_access,
        isAdmin: result.is_admin,
        role: result.role ?? null,
        appAccessLoaded: true,
        installedApps: result.installed_apps ?? [],
        supportOnly: result.support_only ?? false,
      });
    })
    .catch(() => {
      // On error, default to full access (fail-open for UX)
      useAuthStore.setState({ appAccessLoaded: true });
    });
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      frappeUrl: null,
      username: null,
      frappeClient: null,
      hasHydrated: false,
      currentTenant: null,
      tenantSlug: null,
      activeStorefrontKey: null,
      // NOTE: authMode is derived from env var, NOT persisted in localStorage
      // This ensures production/dev environments use correct mode regardless of stored state
      authMode: (import.meta.env.VITE_AUTH_MODE as AuthMode) || 'direct',
      cpToken: null,
      cpTokenExpiresAt: null,
      tenantServerUrl: null,
      tenantCompany: null,
      isProvisioned: false,
      storefrontUrl: null,
      appAccess: null,
      isAdmin: false,
      role: null,
      appAccessLoaded: false,
      installedApps: [],
      supportOnly: false,

      setActiveStorefront: (key: string | null) => {
        set({ activeStorefrontKey: key });
      },

      // Direct mode login (existing Frappe session auth)
      login: async (url: string, username: string, password: string) => {
        const normalizedUrl = url.replace(/\/$/, '');
        const apiUrlForClient = resolveApiUrl(normalizedUrl);

        const client = initFrappeClient({
          url: apiUrlForClient,
          useSession: true,
        });

        const loggedInUser = await client.login(username, password);

        // Phase 5.3: Fetch tenant after login
        const tenant = await fetchUserTenant(client);

        set({
          isAuthenticated: true,
          frappeUrl: normalizedUrl,
          username: loggedInUser,
          frappeClient: client,
          currentTenant: tenant,
        });
      },

      // Logout — works for both direct and control_plane modes.
      // The UI calls this single method; it detects the mode and cleans up accordingly.
      logout: async () => {
        const currentMode = (import.meta.env.VITE_AUTH_MODE as AuthMode) || 'direct';

        if (currentMode === 'control_plane') {
          // Delegate to CP logout (clears individual localStorage keys + Zustand state)
          await get().logoutControlPlane();
          return;
        }

        // Direct mode — clear Frappe session
        const { frappeClient } = get();
        if (frappeClient) {
          try {
            await frappeClient.logout();
          } catch {
            // ignore logout errors
          }
        }
        set({
          isAuthenticated: false,
          frappeUrl: null,
          username: null,
          frappeClient: null,
          currentTenant: null,
          activeStorefrontKey: null,
        });
      },

      // Control Plane mode login (JWT auth)
      loginViaControlPlane: async (email: string, password: string) => {
        const response = await cpLogin(email, password);

        if (!response.success) {
          throw new Error('Login failed');
        }

        // Decode actual expiry from JWT token (server sets the lifetime)
        const expiresAt = decodeJwtExpiry(response.access_token)
          || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

        // Store tokens in localStorage for API calls
        setCPToken(response.access_token, expiresAt, response.refresh_token);
        // Store tenant metadata as individual keys (survives blob corruption)
        setCPTenantMeta(response.server_url, response.is_provisioned, response.storefront_url || null);

        set({
          isAuthenticated: true,
          username: response.user_id,
          cpToken: response.access_token,
          cpTokenExpiresAt: expiresAt,
          currentTenant: response.tenant_id,
          tenantSlug: response.tenant_slug || null,
          tenantServerUrl: response.server_url,
          tenantCompany: sanitizeCompanyDocname(response.company, response.tenant_id),
          isProvisioned: response.is_provisioned,
          storefrontUrl: response.storefront_url || null,
          // Clear Frappe-specific fields
          frappeUrl: null,
          frappeClient: null,
          // Reset store scope on fresh login (prevents stale key from different tenant)
          activeStorefrontKey: null,
        });

        // Fetch app access for sidebar gating
        fetchAndStoreAppAccess();
      },

      // Generic CP token response login (for Google One Tap, future providers)
      loginWithCPResponse: (response: CPTokenResponse) => {
        const expiresAt = decodeJwtExpiry(response.access_token)
          || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

        setCPToken(response.access_token, expiresAt, response.refresh_token);
        setCPTenantMeta(response.server_url, response.is_provisioned, response.storefront_url || null);

        set({
          isAuthenticated: true,
          username: response.user_id,
          cpToken: response.access_token,
          cpTokenExpiresAt: expiresAt,
          currentTenant: response.tenant_id,
          tenantSlug: (response as any).tenant_slug || null,
          tenantServerUrl: response.server_url,
          tenantCompany: sanitizeCompanyDocname(response.company, response.tenant_id),
          isProvisioned: response.is_provisioned,
          storefrontUrl: response.storefront_url || null,
          frappeUrl: null,
          frappeClient: null,
          activeStorefrontKey: null,
        });

        fetchAndStoreAppAccess();
      },

      // LINE Login (via OAuth callback params)
      loginViaLine: (params: LineCallbackParams) => {
        // Use backend-provided expiry, then try decoding JWT, then fallback to 24h
        const expiresAt = params.expires_at
          || decodeJwtExpiry(params.access_token)
          || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

        // Store tokens in localStorage for API calls
        setCPToken(params.access_token, expiresAt, params.refresh_token);
        // Store tenant metadata as individual keys (survives blob corruption)
        setCPTenantMeta(params.server_url, params.is_provisioned === '1', params.storefront_url || null);

        set({
          isAuthenticated: true,
          username: params.user_id,
          cpToken: params.access_token,
          cpTokenExpiresAt: expiresAt,
          currentTenant: params.tenant_id,
          tenantSlug: params.tenant_slug || null,
          tenantServerUrl: params.server_url,
          tenantCompany: sanitizeCompanyDocname(params.company, params.tenant_id),
          isProvisioned: params.is_provisioned === '1',
          storefrontUrl: params.storefront_url || null,
          // Clear Frappe-specific fields
          frappeUrl: null,
          frappeClient: null,
          // Reset store scope on fresh login (prevents stale key from different tenant)
          activeStorefrontKey: null,
        });

        // Fetch app access for sidebar gating
        fetchAndStoreAppAccess();
      },

      // Control Plane mode logout
      logoutControlPlane: async () => {
        try {
          await cpLogout();
        } catch {
          // Ignore logout errors (token might already be invalid)
        }

        clearCPToken();
        // Clear Chatwoot session flags so next /inbox visit triggers fresh SSO (OPS-1489)
        clearChatwootSession();
        // Clear Ghost session flags so next /blog visit triggers fresh SSO
        clearGhostSession();
        clearTwentySession();

        set({
          isAuthenticated: false,
          username: null,
          cpToken: null,
          cpTokenExpiresAt: null,
          currentTenant: null,
          tenantSlug: null,
          tenantServerUrl: null,
          tenantCompany: null,
          isProvisioned: false,
          storefrontUrl: null,
          frappeUrl: null,
          frappeClient: null,
          appAccess: null,
          isAdmin: false,
          role: null,
          appAccessLoaded: false,
          installedApps: [],
          supportOnly: false,
          activeStorefrontKey: null,
        });
      },
    }),
    {
      name: "frappe-auth-storage",
      skipHydration: true,   // REQUIRED
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        frappeUrl: state.frappeUrl,
        username: state.username,
        currentTenant: state.currentTenant,
        tenantSlug: state.tenantSlug,
        activeStorefrontKey: state.activeStorefrontKey,
        // NOTE: DO NOT persist authMode - it comes from env var
        // This prevents dev localStorage from overriding prod env var
        cpToken: state.cpToken,
        cpTokenExpiresAt: state.cpTokenExpiresAt,
        tenantServerUrl: state.tenantServerUrl,
        tenantCompany: state.tenantCompany,
        isProvisioned: state.isProvisioned,
        storefrontUrl: state.storefrontUrl,
        appAccess: state.appAccess,
        isAdmin: state.isAdmin,
        role: state.role,
        appAccessLoaded: state.appAccessLoaded,
        installedApps: state.installedApps,
        supportOnly: state.supportOnly,
      }),

      onRehydrateStorage: () => (state) => {
        // Get current auth mode from env var (NOT from persisted state)
        const currentAuthMode = (import.meta.env.VITE_AUTH_MODE as AuthMode) || 'direct';

        // Control Plane mode — individual localStorage keys are the source of truth,
        // NOT the Zustand persist blob (which can get out of sync).
        if (currentAuthMode === 'control_plane') {
          const storedToken = getCPToken();
          const refreshToken = getCPRefreshToken();
          const expiresAt = state?.cpTokenExpiresAt
            || localStorage.getItem('control_plane_jwt_expiry');
          const isExpired = expiresAt && new Date(expiresAt) < new Date();

          // Migration: backfill individual localStorage keys from blob for sessions
          // that logged in before QF#47 added setCPTenantMeta to login methods
          if (!getCPServerUrl() && state?.tenantServerUrl) {
            setCPTenantMeta(
              state.tenantServerUrl,
              state.isProvisioned ?? false,
              state.storefrontUrl ?? null,
            );
          }

          if (!storedToken && !refreshToken) {
            // No tokens at all — user is genuinely logged out
            clearCPToken();
            useAuthStore.setState({
              hasHydrated: true,
              isAuthenticated: false,
              cpToken: null,
              cpTokenExpiresAt: null,
              username: null,
              currentTenant: null,
              tenantSlug: null,
              tenantCompany: null,
              tenantServerUrl: null,
              isProvisioned: false,
              storefrontUrl: null,
              frappeUrl: null,
              frappeClient: null,
              activeStorefrontKey: null,
            });
          } else if (storedToken && !isExpired) {
            // Valid, non-expired token exists — user IS authenticated.
            // Recovery priority: JWT payload → individual localStorage keys → blob
            const payload = decodeJwtPayload(storedToken);
            const serverUrl = (payload?.server_url as string) || getCPServerUrl() || state?.tenantServerUrl || null;
            const provisioned = (payload?.is_provisioned as boolean) || getCPIsProvisioned() || state?.isProvisioned || false;
            const sfUrl = (payload?.storefront_url as string) || getCPStorefrontUrl() || state?.storefrontUrl || null;
            // Backfill individual localStorage keys from JWT (one-time migration)
            if (serverUrl && !getCPServerUrl()) {
              setCPTenantMeta(serverUrl, provisioned, sfUrl);
            }
            useAuthStore.setState({
              hasHydrated: true,
              isAuthenticated: true,
              cpToken: storedToken,
              cpTokenExpiresAt: expiresAt,
              username: (payload?.user as string) || state?.username || null,
              currentTenant: (payload?.tenant as string) || state?.currentTenant || null,
              tenantCompany: sanitizeCompanyDocname(
                (payload?.company as string) || state?.tenantCompany || null,
                (payload?.tenant as string) || state?.currentTenant || null,
              ),
              tenantSlug: (payload?.slug as string) || state?.tenantSlug || null,
              isProvisioned: provisioned,
              tenantServerUrl: serverUrl,
              storefrontUrl: sfUrl,
              activeStorefrontKey: state?.activeStorefrontKey ?? null,
            });
            // Fetch app access for sidebar gating (non-blocking)
            fetchAndStoreAppAccess();
          } else {
            // Token expired or missing, but refresh token exists — silent refresh.
            // Decode expired token for identity (claims are still valid, only exp has passed).
            const expiredPayload = storedToken ? decodeJwtPayload(storedToken) : null;
            const expServerUrl = (expiredPayload?.server_url as string) || getCPServerUrl() || state?.tenantServerUrl || null;
            const expProvisioned = (expiredPayload?.is_provisioned as boolean) || getCPIsProvisioned() || state?.isProvisioned || false;
            const expSfUrl = (expiredPayload?.storefront_url as string) || getCPStorefrontUrl() || state?.storefrontUrl || null;
            if (expServerUrl && !getCPServerUrl()) {
              setCPTenantMeta(expServerUrl, expProvisioned, expSfUrl);
            }
            useAuthStore.setState({
              hasHydrated: true,
              isAuthenticated: true,
              username: (expiredPayload?.user as string) || state?.username || null,
              currentTenant: (expiredPayload?.tenant as string) || state?.currentTenant || null,
              tenantCompany: sanitizeCompanyDocname(
                (expiredPayload?.company as string) || state?.tenantCompany || null,
                (expiredPayload?.tenant as string) || state?.currentTenant || null,
              ),
              tenantSlug: (expiredPayload?.slug as string) || state?.tenantSlug || null,
              isProvisioned: expProvisioned,
              tenantServerUrl: expServerUrl,
              storefrontUrl: expSfUrl,
              activeStorefrontKey: state?.activeStorefrontKey ?? null,
            });
            _refreshPromise = (async () => {
              try {
                const result = await refreshCPToken();
                if (result) {
                  // Refresh succeeded — decode new JWT for authoritative identity + metadata
                  const payload = decodeJwtPayload(result.access_token);
                  const newServerUrl = (payload?.server_url as string) || expServerUrl;
                  const newProvisioned = (payload?.is_provisioned as boolean) || expProvisioned;
                  const newSfUrl = (payload?.storefront_url as string) || expSfUrl;
                  if (newServerUrl) setCPTenantMeta(newServerUrl, newProvisioned, newSfUrl);
                  useAuthStore.setState({
                    cpToken: result.access_token,
                    cpTokenExpiresAt: result.expires_at,
                    username: (payload?.user as string) || state?.username || null,
                    currentTenant: (payload?.tenant as string) || state?.currentTenant || null,
                    tenantCompany: sanitizeCompanyDocname(
                      (payload?.company as string) || state?.tenantCompany || null,
                      (payload?.tenant as string) || state?.currentTenant || null,
                    ),
                    tenantSlug: (payload?.slug as string) || state?.tenantSlug || null,
                    isProvisioned: newProvisioned,
                    tenantServerUrl: newServerUrl,
                    storefrontUrl: newSfUrl,
                    activeStorefrontKey: state?.activeStorefrontKey ?? null,
                  });
                  // Fetch app access after successful refresh
                  fetchAndStoreAppAccess();
                } else {
                  clearCPToken();
                  useAuthStore.setState({
                    isAuthenticated: false,
                    cpToken: null,
                    cpTokenExpiresAt: null,
                    username: null,
                    currentTenant: null,
                    tenantSlug: null,
                    tenantCompany: null,
                    tenantServerUrl: null,
                    isProvisioned: false,
                    storefrontUrl: null,
                    frappeUrl: null,
                    frappeClient: null,
                    activeStorefrontKey: null,
                  });
                }
              } catch {
                clearCPToken();
                useAuthStore.setState({
                  isAuthenticated: false,
                  cpToken: null,
                  cpTokenExpiresAt: null,
                  username: null,
                  currentTenant: null,
                  tenantSlug: null,
                  tenantCompany: null,
                  tenantServerUrl: null,
                  isProvisioned: false,
                  storefrontUrl: null,
                  frappeUrl: null,
                  frappeClient: null,
                  activeStorefrontKey: null,
                });
              } finally {
                _refreshPromise = null;
              }
            })();
          }
          return; // CP mode handled — skip direct mode checks
        }

        // Direct mode — mark hydration complete
        useAuthStore.setState({ hasHydrated: true });
        if (state?.isAuthenticated) {
          // Direct mode - restore Frappe client
          if (currentAuthMode === 'direct' && state.frappeUrl) {
            const apiUrlForClient = resolveApiUrl(state.frappeUrl);

            const client = initFrappeClient({
              url: apiUrlForClient,
              useSession: true,
            });

            // Phase 5.3: Fetch fresh tenant on rehydration
            // Use async IIFE since onRehydrateStorage callback is sync
            (async () => {
              try {
                const tenant = await fetchUserTenant(client);
                useAuthStore.setState({
                  frappeClient: client,
                  currentTenant: tenant,
                });
              } catch {
                // If fetch fails, just set the client with persisted tenant
                useAuthStore.setState({ frappeClient: client });
              }
            })();
          }
        }
      },
    }
  )
);

/**
 * Wait for any in-flight token refresh to complete, then return the current token.
 * Iframe embeds (Builder, CRM, Insights) must call this before building the SSO
 * form to avoid submitting an expired JWT that causes 401.
 *
 * Returns the fresh token string or null if not authenticated.
 */
export async function ensureFreshToken(): Promise<string | null> {
  if (_refreshPromise) {
    await _refreshPromise;
  }
  return getCPToken();
}

// Sync token refreshes from cpGuestApiCall into Zustand store.
// This avoids a circular import (authStore → controlPlaneClient → authStore).
setTokenRefreshCallback((token, expiresAt) => {
  useAuthStore.setState({ cpToken: token, cpTokenExpiresAt: expiresAt });
});
